import type { TableActionGroup } from "@shared/types/data-table"
import type { Quote } from "@shared/types/quotes"
import { buildGroupedRowActions } from "./build-grouped-actions"

export interface QuoteActionHandlers {
  onSendQuote?: (quote: Quote) => void
  onMarkAsSigned?: (quote: Quote) => void
  onDownloadQuote?: (quote: Quote) => void
  onShareQuoteLink?: (quote: Quote) => void
  onViewQuote?: (quote: Quote) => void
  onPreview?: (quote: Quote) => void
  onDuplicateQuote?: (quote: Quote) => void
  onCancelQuote?: (quote: Quote) => void
  onViewCustomer?: (quote: Quote) => void
  onBulkExport?: (quoteIds: string[]) => void
  onBulkCancel?: (quoteIds: string[]) => void
}

/**
 * Row actions for quote tables
 * Uses buildGroupedRowActions for standardized VIEW → ACTIONS → LINKS grouping
 */
export const getQuoteRowActions = (handlers: QuoteActionHandlers = {}): TableActionGroup<Quote>[] =>
  buildGroupedRowActions<Quote>({
    view: [
      {
        key: "preview",
        label: "Preview",
        onClick: (quote) => handlers.onPreview?.(quote),
      },
    ],
    actions: [
      {
        key: "sendQuote",
        label: "Send quote",
        onClick: (quote) => handlers.onSendQuote?.(quote),
        condition: (quote) => quote.status !== 'cancelled',
      },
      {
        key: "shareQuoteLink",
        label: "Share quote link",
        onClick: (quote) => handlers.onShareQuoteLink?.(quote),
      },
      {
        key: "markAsSigned",
        label: "Mark as signed",
        onClick: (quote) => handlers.onMarkAsSigned?.(quote),
        condition: (quote) => quote.status !== 'cancelled' && quote.status !== 'accepted',
      },
      {
        key: "downloadQuote",
        label: "Download quote",
        onClick: (quote) => handlers.onDownloadQuote?.(quote),
      },
      {
        key: "duplicateQuote",
        label: "Duplicate quote",
        onClick: (quote) => handlers.onDuplicateQuote?.(quote),
      },
      {
        key: "cancelQuote",
        label: "Cancel quote",
        onClick: (quote) => handlers.onCancelQuote?.(quote),
        variant: "destructive" as const,
        condition: (quote) => quote.status !== 'cancelled',
      },
    ],
    links: [
      {
        key: "viewCustomer",
        label: "View customer",
        onClick: (quote) => handlers.onViewCustomer?.(quote),
      },
    ],
  })

/**
 * Panel actions for quote detail pages
 * Same as row actions for consistency
 */
export const getQuotePanelActions = (handlers: QuoteActionHandlers = {}): TableActionGroup<Quote>[] =>
  getQuoteRowActions(handlers)

/**
 * Bulk actions for quote tables
 */
export const getQuoteBulkActions = (handlers: QuoteActionHandlers = {}) => [
  {
    key: "export",
    label: "Export",
    onClick: (ids: string[]) => handlers.onBulkExport?.(ids),
    disabled: (ids: string[]) => ids.length === 0,
  },
  {
    key: "cancel",
    label: "Cancel",
    onClick: (ids: string[]) => handlers.onBulkCancel?.(ids),
    disabled: (ids: string[]) => ids.length === 0,
  },
]

/**
 * Hover actions for quote tables
 */
export const getQuoteHoverActions = (handlers: QuoteActionHandlers = {}) => [
  {
    key: "viewQuote",
    label: "View quote",
    onClick: (quote: Quote) => handlers.onViewQuote?.(quote),
  },
]
